import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { agrogestao } from '@/api/agrogestaoClient';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import ExportacaoRelatorios from "@/components/financeiro/ExportacaoRelatorios";

export function ContasReceberAdmin() {
    const [busca, setBusca] = useState('');
    const [atrasoFiltro, setAtrasoFiltro] = useState('todos');
    const [clienteEmailSel, setClienteEmailSel] = useState('');
    const [renegOpen, setRenegOpen] = useState(false);
    const [renegFat, setRenegFat] = useState(null);
    const [renegNovoVencimento, setRenegNovoVencimento] = useState('');
    const [renegDesconto, setRenegDesconto] = useState('');
    const [renegLoading, setRenegLoading] = useState(false);
    const [renegError, setRenegError] = useState('');

    const { data: faturas = [], isLoading } = useQuery({
        queryKey: ['contas_receber_admin'],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('faturas')
                .select('id, descricao, status, valor_centavos, propriedade_id, created_date, vencimento, paga_em, pix_brcode, dunning_nivel')
                .order('created_date', { ascending: false })
                .limit(1000);
            if (error) return [];
            return data || [];
        }
    });

    const { data: propriedades = [] } = useQuery({
        queryKey: ['props_for_contas_receber_admin'],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('propriedades')
                .select('id, nome, created_by')
                .order('nome', { ascending: true })
                .limit(1000);
            if (error) return [];
            return data || [];
        }
    });

    const { data: licencas = [] } = useQuery({
        queryKey: ['licencas_status_admin'],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('licencas')
                .select('id, propriedade_id, status')
                .order('valid_until', { ascending: false })
                .limit(2000);
            if (error) return [];
            return data || [];
        }
    });

    const propsById = useMemo(() => {
        const m = {};
        (propriedades || []).forEach(p => { m[String(p.id)] = p; });
        return m;
    }, [propriedades]);

    const clientesEmails = useMemo(() => {
        const s = new Set();
        (propriedades || []).forEach(p => { if (p.created_by) s.add(String(p.created_by)); });
        return Array.from(s).sort();
    }, [propriedades]);

    const atrasoDias = (f) => {
        const d = f.vencimento ? new Date(f.vencimento) : null;
        if (!d) return 0;
        const now = new Date();
        const ms = now.getTime() - d.getTime();
        const days = Math.floor(ms / (1000 * 60 * 60 * 24));
        return days > 0 ? days : 0;
    };

    const filtered = useMemo(() => {
        return (faturas || []).filter(f => {
            const st = String(f.status || '');
            if (!['pendente', 'expirado'].includes(st)) return false;
            if (busca && !String(f.descricao || '').toLowerCase().includes(busca.toLowerCase())) return false;
            if (clienteEmailSel) {
                const p = propsById[String(f.propriedade_id || '')];
                if (!p || String(p.created_by || '') !== clienteEmailSel) return false;
            }
            if (atrasoFiltro !== 'todos') {
                const d = atrasoDias(f);
                if (atrasoFiltro === '1_7' && !(d >= 1 && d <= 7)) return false;
                if (atrasoFiltro === '8_30' && !(d >= 8 && d <= 30)) return false;
                if (atrasoFiltro === '30_plus' && !(d >= 31)) return false;
            }
            return true;
        });
    }, [faturas, busca, atrasoFiltro, clienteEmailSel, propsById]);

    const reenviarPix = async (f) => {
        try {
            const subj = `Lembrete de pagamento - Fatura #${f.id}`;
            const body = `Olá!\n\nTemos uma fatura pendente no valor de ${(Number(f.valor_centavos || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.\nDescrição: ${f.descricao || ''}.\n\nCódigo PIX (copia e cola): ${f.pix_brcode || ''}.\n\nObrigado!`;
            const p = propsById[String(f.propriedade_id || '')];
            const email = p?.created_by || '';
            if (email) {
                await agrogestao.integrations.Core.SendEmail({ to: email, subject: subj, content: body });
            }
            const next = new Date();
            next.setDate(next.getDate() + 3);
            await agrogestao.client
                .from('faturas')
                .update({ dunning_nivel: Number(f.dunning_nivel || 0) + 1, dunning_ultimo_contato: new Date().toISOString(), dunning_proximo_contato: next.toISOString() })
                .eq('id', f.id);
        } catch (e) {
            alert('Falha ao reenviar PIX');
        }
    };

    const aplicarRenegociacao = async () => {
        try {
            setRenegError('');
            setRenegLoading(true);
            if (!renegFat?.id) throw new Error('Fatura inválida');
            const updates = {};
            if (renegNovoVencimento) updates.vencimento = new Date(renegNovoVencimento).toISOString();
            if (renegDesconto) {
                const perc = Number(renegDesconto || 0);
                if (perc > 0) {
                    const novo = Math.round(Number(renegFat.valor_centavos || 0) * (1 - (perc / 100)));
                    updates.valor_centavos = novo;
                }
            }
            await agrogestao.client.from('faturas').update(updates).eq('id', renegFat.id);
            try { await agrogestao.access.logAudit({ table: 'faturas', acao: 'renegociar', details: { fatura_id: renegFat.id, novo_vencimento: updates.vencimento || null, desconto_percentual: Number(renegDesconto || 0) }, status: 'success' }); } catch { }
            setRenegOpen(false);
            setRenegFat(null);
            setRenegNovoVencimento('');
            setRenegDesconto('');
        } catch (e) {
            setRenegError(e?.message || 'Falha na renegociação');
        } finally {
            setRenegLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
                <Input placeholder="Buscar descrição" value={busca} onChange={(e) => setBusca(e.target.value)} className="w-[300px]" />
                <Select value={atrasoFiltro} onValueChange={setAtrasoFiltro}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todos">Todos os atrasos</SelectItem>
                        <SelectItem value="1_7">1–7 dias</SelectItem>
                        <SelectItem value="8_30">8–30 dias</SelectItem>
                        <SelectItem value="30_plus">30+ dias</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={clienteEmailSel || 'todos'} onValueChange={(v) => setClienteEmailSel(v === 'todos' ? '' : v)}>
                    <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todos">Todos os clientes</SelectItem>
                        {clientesEmails.map(e => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
                    </SelectContent>
                </Select>
                <div className="ml-auto">
                    <ExportacaoRelatorios
                        dados={filtered.map(f => ({ ...f, valor: Number(f.valor_centavos || 0) / 100 }))}
                        nomeArquivo="contas_a_receber"
                        colunas={[
                            { label: 'Criada', accessor: (f) => f.created_date, type: 'date' },
                            { label: 'Propriedade', accessor: (f) => { const p = propsById[String(f.propriedade_id || '')]; return p?.nome || String(f.propriedade_id || ''); } },
                            { label: 'Descrição', accessor: (f) => f.descricao },
                            { label: 'Valor', accessor: (f) => f.valor, type: 'currency' },
                            { label: 'Status', accessor: (f) => f.status },
                            { label: 'Dunning', accessor: (f) => Number(f.dunning_nivel || 0) },
                            { label: 'Vencimento', accessor: (f) => f.vencimento, type: 'date' },
                            { label: 'AtrasoDias', accessor: (f) => atrasoDias(f) }
                        ]}
                    />
                </div>
            </div>
            {(() => {
                const suspensas = (licencas || []).filter(l => String(l.status || '') === 'bloqueado').length; return suspensas > 0 ? (
                    <Card className="bg-rose-50 border-rose-200">
                        <CardHeader><CardTitle className="text-sm">Planos suspensos</CardTitle></CardHeader>
                        <CardContent className="text-sm">{suspensas} propriedade(s) com plano suspenso</CardContent>
                    </Card>
                ) : null;
            })()}
            <div className="rounded-md border">
                <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-stone-600 font-medium">
                        <tr>
                            <th className="text-left py-3 px-4">Criada</th>
                            <th className="text-left py-3 px-4">Propriedade</th>
                            <th className="text-left py-3 px-4">Descrição</th>
                            <th className="text-right py-3 px-4">Valor</th>
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">Vencimento</th>
                            <th className="text-left py-3 px-4">Atraso</th>
                            <th className="text-left py-3 px-4">Dunning</th>
                            <th className="text-left py-3 px-4">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {isLoading ? (
                            <tr><td colSpan={9} className="py-8 text-center text-stone-500">Carregando...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={9} className="py-8 text-center text-stone-500">Sem contas a receber pendentes</td></tr>
                        ) : filtered.map(f => (
                            <tr key={f.id} className="hover:bg-stone-50">
                                <td className="py-3 px-4 whitespace-nowrap">{f.created_date ? format(new Date(f.created_date), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                <td className="py-3 px-4 max-w-[200px] truncate" title={(() => { const p = propsById[String(f.propriedade_id || '')]; return p?.nome || String(f.propriedade_id || ''); })()}>
                                    {(() => { const p = propsById[String(f.propriedade_id || '')]; return p?.nome || String(f.propriedade_id || ''); })()}
                                </td>
                                <td className="py-3 px-4">{f.descricao || '-'}</td>
                                <td className="py-3 px-4 text-right font-medium text-stone-900">{(Number(f.valor_centavos || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                <td className="py-3 px-4">{f.status}</td>
                                <td className="py-3 px-4">{f.vencimento ? format(new Date(f.vencimento), 'dd/MM/yyyy') : '-'}</td>
                                <td className="py-3 px-4">{atrasoDias(f)} dia(s)</td>
                                <td className="py-3 px-4">{Number(f.dunning_nivel || 0)}</td>
                                <td className="py-3 px-4">
                                    <div className="flex gap-2">
                                        <Button variant="outline" size="sm" onClick={() => reenviarPix(f)}>Pix</Button>
                                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => { setRenegFat(f); setRenegOpen(true); }}>Renegociar</Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Dialog open={renegOpen} onOpenChange={setRenegOpen}>
                <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Renegociação</DialogTitle>
                        <DialogDescription></DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label>Novo vencimento</Label>
                            <Input type="date" value={renegNovoVencimento} onChange={(e) => setRenegNovoVencimento(e.target.value)} />
                        </div>
                        <div>
                            <Label>Desconto (%)</Label>
                            <Input type="number" step="0.01" value={renegDesconto} onChange={(e) => setRenegDesconto(e.target.value)} />
                        </div>
                    </div>
                    {renegError && <div className="text-red-600 text-sm">{renegError}</div>}
                    <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => { setRenegOpen(false); setRenegFat(null); setRenegNovoVencimento(''); setRenegDesconto(''); }}>Cancelar</Button>
                        <Button className="bg-green-600 hover:bg-green-700" onClick={aplicarRenegociacao} disabled={renegLoading}>{renegLoading ? 'Salvando…' : 'Aplicar'}</Button>
                    </div>
                </DialogContent>
            </Dialog>
            {clienteEmailSel && (
                <Card className="bg-amber-50 border-amber-200">
                    <CardHeader><CardTitle className="text-sm">Histórico de pagamentos</CardTitle></CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2 px-2">Data</th>
                                        <th className="text-left py-2 px-2">Propriedade</th>
                                        <th className="text-left py-2 px-2">Descrição</th>
                                        <th className="text-right py-2 px-2">Valor</th>
                                        <th className="text-left py-2 px-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const list = (faturas || []).filter(ff => {
                                            const p = propsById[String(ff.propriedade_id || '')];
                                            return p && String(p.created_by || '') === clienteEmailSel && String(ff.status || '') === 'pago';
                                        }).slice(0, 20);
                                        if (list.length === 0) return (<tr><td colSpan={5} className="py-4 text-center text-stone-500">Sem pagamentos</td></tr>);
                                        return list.map(ff => (
                                            <tr key={ff.id} className="border-b">
                                                <td className="py-2 px-2">{ff.paga_em ? format(new Date(ff.paga_em), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                                <td className="py-2 px-2">{(() => { const p = propsById[String(ff.propriedade_id || '')]; return p?.nome || String(ff.propriedade_id || ''); })()}</td>
                                                <td className="py-2 px-2">{ff.descricao || '-'}</td>
                                                <td className="py-2 px-2 text-right">{(Number(ff.valor_centavos || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                <td className="py-2 px-2">{ff.status || '-'}</td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
